import React, { useEffect, useState, useMemo } from 'react'
import {
    Card, Table, Tag, Input, DatePicker, Select, Row, Col,
    Statistic, Badge, Avatar, Tooltip, Space, Typography, Progress,
    Empty, Divider
} from 'antd'
import {
    SearchOutlined, CalendarOutlined, FilterOutlined,
    HomeOutlined, UserOutlined, ThunderboltOutlined,
    RiseOutlined, ShoppingOutlined, SortAscendingOutlined
} from '@ant-design/icons'
import { ExpenseService } from '../../Services/ExpenseService'

import { canViewModule } from '../../../utils/permissionUtils';

const MODULE_KEY = "service_requests";

const { RangePicker } = DatePicker
const { Option } = Select
const { Text, Title } = Typography

// ── Status config ─────────────────────────────────────────────
const STATUS_CONFIG = {
    occupied: { color: '#10b981', bg: '#ecfdf5', label: 'Occupied' },
    check_out: { color: '#6366f1', bg: '#eef2ff', label: 'Checked Out' },
    reserved: { color: '#f59e0b', bg: '#fffbeb', label: 'Reserved' },
    cancel: { color: '#ef4444', bg: '#fef2f2', label: 'Cancelled' },
    pending: { color: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
    paid: { color: '#10b981', bg: '#ecfdf5', label: 'Paid' },
    completed: { color: '#6366f1', bg: '#eef2ff', label: 'Completed' },
}

const StatusTag = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { color: '#94a3b8', bg: '#f8fafc', label: status }
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}30`,
            letterSpacing: '0.02em', whiteSpace: 'nowrap'
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
            {cfg.label}
        </span>
    )
}

// ── Metric Card ───────────────────────────────────────────────
const MetricCard = ({ icon, label, value, sub, color }) => (
    <div style={{
        background: '#fff', borderRadius: 14, padding: '18px 22px',
        border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    }}>
        <div style={{
            width: 48, height: 48, borderRadius: 12, background: `${color}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, color
        }}>{icon}</div>
        <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: color, marginTop: 1, fontWeight: 600 }}>{sub}</div>}
        </div>
    </div>
)

// ── Main Component ────────────────────────────────────────────
const ServiceReqPage = () => {
    const [serviceReq, setServiceReq] = useState([])
    const [search, setSearch] = useState('')
    const [dateRange, setDateRange] = useState(null)
    const [statusFilter, setStatus] = useState('all')
    const [sortBy, setSortBy] = useState('requests_desc')
    const [expandedRow, setExpandedRow] = useState(null)

    // ── Sample data fallback (remove when API works) ──────────
    const SAMPLE = [{ "id": 1, "log": [{ "id": 10, "room_id": 1, "book_room_id": 12, "check_in": "2026-04-04", "check_out": "2026-04-06", "check_in_time": "02:36:19 PM", "check_out_time": "02:36:19 PM", "act_check_out_date": "06-Apr-2026", "act_check_out_time": "2:27:27 PM", "status": "check_out", "note": "cleared", "book_room": { "id": 12, "guest_name": "sanjeva", "phone": "98743456789", "email": "saneva@gmail.com", "id_type": "Aadhaar", "id_number": "567876567", "address": "teagjd,shcjsbcjs,hjsbch8987778", "guests": 4, "check_in": "2026-04-04", "check_out": "2026-04-06", "special_requests": "", "payment_mode": "Card", "room_id": 1, "room_name": "2 + 1 Room king suite", "room_number": "11A", "room_type": "AC | Delux", "floor": "1", "price_per_day": "4567.00", "price_per_hour": "567.00", "max_occupancy": 3, "add_cost_per_occupancy": "500.00", "rate_per_night": "4567.00", "duration_nights": 2, "base_total": "9134.00", "extra_guests": 1, "extra_cost_per_night": "500.00", "extra_total": "1000.00", "total_amount": "10134.00", "created_at": "2026-04-04 09:06:19", "update_at": "06-Apr-2026, 2:27:27 PM", "guest_status": "check_out", "document_upload": "[]", "ac_check_out": null, "ac_check_out_time": null }, "service_requests": [] }] }, { "id": 2, "log": [{ "id": 5, "room_id": 2, "book_room_id": 5, "check_in": "2026-03-30", "check_out": "2026-03-31", "status": "check_out", "note": "passed!!!", "book_room": { "id": 5, "guest_name": "sharath", "phone": "876545678", "email": "sharath@gmail.com", "id_type": "Aadhaar", "id_number": "5678987654", "address": "kuluru, puttur", "guests": 2, "check_in": "2026-03-30", "check_out": "2026-03-31", "special_requests": "AC, Hot water for drinking", "payment_mode": "Cash", "room_id": 2, "room_name": "Couple room", "room_number": "12", "room_type": "AC | Non-AC", "floor": "1", "price_per_day": "2000.00", "price_per_hour": "800.00", "max_occupancy": 2, "add_cost_per_occupancy": "0.00", "rate_per_night": "2000.00", "duration_nights": 1, "base_total": "2000.00", "extra_guests": 0, "extra_cost_per_night": "0.00", "extra_total": "0.00", "total_amount": "2000.00", "created_at": "2026-03-30 11:22:41", "update_at": "31-Mar-2026, 5:20:05 PM", "guest_status": "check_out", "document_upload": "[]", "ac_check_out": null, "ac_check_out_time": null }, "service_requests": [] }, { "id": 12, "room_id": 2, "book_room_id": 14, "check_in": "2026-04-04", "check_out": "2026-04-06", "status": "occupied", "note": null, "book_room": { "id": 14, "guest_name": "bayankar", "phone": "788876543", "email": "gabs@gmail.com", "id_type": "Aadhaar", "id_number": "678908765676", "address": "Foarm howys e, layways, banglore -01", "guests": 3, "check_in": "2026-04-06", "check_out": "2026-04-07", "special_requests": "", "payment_mode": "UPI", "room_id": 2, "room_name": "Couple room", "room_number": "12", "room_type": "AC | Non-AC", "floor": "1", "price_per_day": "2000.00", "price_per_hour": "800.00", "max_occupancy": 2, "add_cost_per_occupancy": "200.00", "rate_per_night": "2000.00", "duration_nights": 1, "base_total": "2000.00", "extra_guests": 1, "extra_cost_per_night": "200.00", "extra_total": "200.00", "total_amount": "2200.00", "created_at": "2026-04-06 06:21:15", "update_at": "2026-04-06 06:21:52", "guest_status": "occupied", "document_upload": "[]", "ac_check_out": null, "ac_check_out_time": null }, "service_requests": [{ "sr_id": "7", "service_type": "inhouse", "service_name": null, "inhouse_service_name": "Hospitality", "category": "House keeping", "price": "1200.00", "quantity": 1, "total_amount": "1200.00", "status": "pending", "br_id": 14, "bl_id": 12, "created_at": "10-Apr-2026, 4:24:41 PM", "updated_at": "10-Apr-2026, 4:24:41 PM" }] }] }, { "id": 3, "log": [{ "id": 13, "room_id": 3, "book_room_id": 15, "check_in": "2026-04-06", "check_out": "2026-04-07", "status": "occupied", "note": null, "book_room": { "id": 15, "guest_name": "tommey", "phone": "78908765678", "email": "tyfgv@gmail.com", "id_type": "Aadhaar", "id_number": "6754567877665", "address": "jkdabndbs, hjvcjhds", "guests": 2, "check_in": "2026-04-06", "check_out": "2026-04-07", "special_requests": "aerhdrtrt", "payment_mode": "Cash", "room_id": 3, "room_name": "3 member Family room", "room_number": "13", "room_type": "AC | Non-AC", "floor": "1", "price_per_day": "2000.00", "price_per_hour": "600.00", "max_occupancy": 3, "add_cost_per_occupancy": "122.00", "rate_per_night": "2000.00", "duration_nights": 1, "base_total": "2000.00", "extra_guests": 0, "extra_cost_per_night": "0.00", "extra_total": "0.00", "total_amount": "2000.00", "created_at": "2026-04-06 06:33:04", "update_at": "2026-04-06 06:33:04", "guest_status": "occupied", "document_upload": "[]", "ac_check_out": null, "ac_check_out_time": null }, "service_requests": [{ "sr_id": "8", "service_type": "custom", "service_name": "food(cheken tikka)", "inhouse_service_name": null, "category": "food", "price": "12999.00", "quantity": 1, "total_amount": "12999.00", "status": "pending", "br_id": 15, "bl_id": 13, "created_at": "10-Apr-2026, 4:25:35 PM", "updated_at": "10-Apr-2026, 4:25:35 PM" }, { "sr_id": "9", "service_type": "custom", "service_name": "camp fiire", "inhouse_service_name": null, "category": "utility", "price": "4000.00", "quantity": 1, "total_amount": "4000.00", "status": "pending", "br_id": 15, "bl_id": 13, "created_at": "10-Apr-2026, 4:25:35 PM", "updated_at": "10-Apr-2026, 4:25:35 PM" }] }] }, { "id": 4, "log": [{ "id": 9, "room_id": 4, "book_room_id": 11, "check_in": "2026-04-02", "check_out": "2026-04-03", "status": "occupied", "note": null, "book_room": { "id": 11, "guest_name": "athishay", "phone": "768978657886", "email": "ghj@gs.sdk", "id_type": "Aadhaar", "id_number": "456789087654", "address": "bandahkls, harodsad, 89283", "guests": 4, "check_in": "2026-04-02", "check_out": "2026-04-03", "special_requests": "", "payment_mode": "Cash", "room_id": 4, "room_name": "Game Room Home Gym", "room_number": "15", "room_type": "AC | Non-AC", "floor": "2", "price_per_day": "2000.00", "price_per_hour": "900.00", "max_occupancy": 5, "add_cost_per_occupancy": "0.00", "rate_per_night": "2000.00", "duration_nights": 1, "base_total": "2000.00", "extra_guests": 0, "extra_cost_per_night": "0.00", "extra_total": "0.00", "total_amount": "2000.00", "created_at": "2026-04-04 05:43:45", "update_at": "2026-04-04 05:43:45", "guest_status": "occupied", "document_upload": "[]", "ac_check_out": null, "ac_check_out_time": null }, "service_requests": [{ "sr_id": "1", "service_type": "inhouse", "service_name": null, "inhouse_service_name": "Hospitality", "category": "House keeping", "price": "1200.00", "quantity": 1, "total_amount": "1200.00", "status": "paid", "br_id": 11, "bl_id": 9, "created_at": "2026-04-06T17:32:14", "updated_at": "2026-04-06T17:41:44" }, { "sr_id": "4", "service_type": "custom", "service_name": "tripod for camera", "inhouse_service_name": null, "category": "service", "price": "3000.00", "quantity": 2, "total_amount": "6000.00", "status": "paid", "br_id": 11, "bl_id": 9, "created_at": "2026-04-06T17:32:15", "updated_at": "2026-04-06T17:41:44" }, { "sr_id": "5", "service_type": "inhouse", "service_name": null, "inhouse_service_name": "Bed sheet", "category": "utility", "price": "200.00", "quantity": 3, "total_amount": "600.00", "status": "paid", "br_id": 11, "bl_id": 9, "created_at": "2026-04-06T17:33:37", "updated_at": "2026-04-06T17:41:44" }] }] }]

    const getAllServiceRequest = async () => {
        try {
            const req = await ExpenseService.getAllServiceRequest()
            setServiceReq(req.data)
        } catch (error) {
            console.error(error)
            setServiceReq(SAMPLE) // fallback
        }
    }

    useEffect(() => { getAllServiceRequest() }, [])

    // ── Flatten rooms into table rows ─────────────────────────
    const flatRooms = useMemo(() => {
        return serviceReq.map(room => {
            const allLogs = room.log || []
            const allSR = allLogs.flatMap(l => l.service_requests || [])
            const allGuests = allLogs.map(l => l.book_room).filter(Boolean)
            const totalSpend = allSR.reduce((s, r) => s + parseFloat(r.total_amount || 0), 0)
            const latestLog = allLogs[allLogs.length - 1]
            const latestBR = latestLog?.book_room

            return {
                key: room.id,
                room_id: room.id,
                room_name: latestBR?.room_name || `Room ${room.id}`,
                room_number: latestBR?.room_number || '-',
                room_type: latestBR?.room_type || '-',
                floor: latestBR?.floor || '-',
                total_bookings: allLogs.length,
                total_sr: allSR.length,
                total_spend: totalSpend,
                current_status: latestLog?.status || '-',
                current_guest: latestBR?.guest_name || '-',
                current_guests_count: latestBR?.guests || 0,
                payment_mode: latestBR?.payment_mode || '-',
                logs: allLogs,
                all_sr: allSR,
            }
        })
    }, [serviceReq])

    // ── Max SR count for progress bars ────────────────────────
    const maxSR = useMemo(() => Math.max(...flatRooms.map(r => r.total_sr), 1), [flatRooms])

    // ── Global stats ──────────────────────────────────────────
    const stats = useMemo(() => {
        const allSR = flatRooms.flatMap(r => r.all_sr)
        const totalRev = flatRooms.reduce((s, r) => s + r.total_spend, 0)
        const topRoom = [...flatRooms].sort((a, b) => b.total_sr - a.total_sr)[0]
        return {
            totalRooms: flatRooms.length,
            totalSR: allSR.length,
            totalRevenue: totalRev,
            topRoom: topRoom?.room_name || '-',
            pendingSR: allSR.filter(s => s.status === 'pending').length,
        }
    }, [flatRooms])

    // ── Filter + Sort ─────────────────────────────────────────
    const filtered = useMemo(() => {
        let rows = [...flatRooms]

        if (search) {
            const q = search.toLowerCase()
            rows = rows.filter(r =>
                r.room_name.toLowerCase().includes(q) ||
                r.room_number.toLowerCase().includes(q) ||
                r.current_guest.toLowerCase().includes(q)
            )
        }
        if (statusFilter !== 'all') {
            rows = rows.filter(r => r.current_status === statusFilter)
        }
        if (dateRange && dateRange[0] && dateRange[1]) {
            const from = dateRange[0].startOf('day').valueOf()
            const to = dateRange[1].endOf('day').valueOf()
            rows = rows.filter(r =>
                r.logs.some(l => {
                    const ci = new Date(l.check_in).getTime()
                    return ci >= from && ci <= to
                })
            )
        }

        switch (sortBy) {
            case 'requests_desc': rows.sort((a, b) => b.total_sr - a.total_sr); break
            case 'requests_asc': rows.sort((a, b) => a.total_sr - b.total_sr); break
            case 'spend_desc': rows.sort((a, b) => b.total_spend - a.total_spend); break
            case 'spend_asc': rows.sort((a, b) => a.total_spend - b.total_spend); break
            case 'bookings_desc': rows.sort((a, b) => b.total_bookings - a.total_bookings); break
            default: break
        }
        return rows
    }, [flatRooms, search, statusFilter, dateRange, sortBy])

    // ── SR sub-table columns ───────────────────────────────────
    const srColumns = [
        { title: 'SR ID', dataIndex: 'sr_id', width: 70, render: v => <Text code>#{v}</Text> },
        {
            title: 'Type', dataIndex: 'service_type', width: 90,
            render: v => <Tag color={v === 'inhouse' ? 'blue' : 'purple'} style={{ borderRadius: 10, fontSize: 11 }}>{v}</Tag>
        },
        {
            title: 'Service', width: 160,
            render: r => <Text strong style={{ fontSize: 13 }}>{r.inhouse_service_name || r.service_name || '-'}</Text>
        },
        {
            title: 'Category', dataIndex: 'category', width: 120,
            render: v => <Tag style={{ borderRadius: 10, background: '#f0f9ff', color: '#0369a1', border: 'none', fontSize: 11 }}>{v}</Tag>
        },
        { title: 'Qty', dataIndex: 'quantity', width: 60, align: 'center' },
        {
            title: 'Price', dataIndex: 'price', width: 100,
            render: v => <Text style={{ color: '#64748b' }}>₹{parseFloat(v).toLocaleString()}</Text>
        },
        {
            title: 'Total', dataIndex: 'total_amount', width: 110,
            render: v => <Text strong style={{ color: '#10b981' }}>₹{parseFloat(v).toLocaleString()}</Text>
        },
        { title: 'Status', dataIndex: 'status', width: 110, render: v => <StatusTag status={v} /> },
        {
            title: 'Date', dataIndex: 'created_at', width: 160,
            render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v?.split('T')[0] || v}</Text>
        },
    ]

    // ── Log expandable ─────────────────────────────────────────
    const renderLogDetail = (logs) => (
        <div style={{ padding: '12px 0' }}>
            {logs.map(log => (
                <div key={log.id} style={{
                    background: '#fafbfc', border: '1px solid #e2e8f0',
                    borderRadius: 12, padding: 16, marginBottom: 12
                }}>
                    {/* Booking header */}
                    <Row gutter={16} align="middle" style={{ marginBottom: 10 }}>
                        <Col>
                            <Avatar style={{ background: '#e0e7ff', color: '#4f46e5' }} size={36} icon={<UserOutlined />} />
                        </Col>
                        <Col flex="auto">
                            <Text strong style={{ fontSize: 14, color: '#1e293b' }}>
                                {log.book_room?.guest_name || 'Guest'}
                            </Text>
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                {log.book_room?.phone} · {log.book_room?.payment_mode} · {log.book_room?.guests} guests
                            </div>
                        </Col>
                        <Col>
                            <StatusTag status={log.status} />
                        </Col>
                        <Col>
                            <Text strong style={{ color: '#10b981' }}>
                                ₹{parseFloat(log.book_room?.total_amount || 0).toLocaleString()}
                            </Text>
                        </Col>
                        <Col>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {log.check_in} → {log.check_out}
                            </Text>
                        </Col>
                    </Row>

                    {/* Service Requests */}
                    {log.service_requests?.length > 0 ? (
                        <Table
                            columns={srColumns}
                            dataSource={log.service_requests.map((s, i) => ({ ...s, key: i }))}
                            pagination={false}
                            size="small"
                            style={{ borderRadius: 8, overflow: 'hidden' }}
                        />
                    ) : (
                        <div style={{
                            textAlign: 'center', padding: '12px 0',
                            color: '#94a3b8', fontSize: 13, fontStyle: 'italic'
                        }}>
                            No service requests for this booking
                        </div>
                    )}
                </div>
            ))}
        </div>
    )

    // ── Main table columns ─────────────────────────────────────
    const columns = [
        {
            title: 'Room',
            width: 220,
            render: r => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 10,
                        background: 'linear-gradient(135deg, #e0e7ff, #dbeafe)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, color: '#4f46e5', fontWeight: 700, flexShrink: 0
                    }}>
                        {r.room_number}
                    </div>
                    <div>
                        <Text strong style={{ fontSize: 13, color: '#1e293b', display: 'block', lineHeight: 1.3 }}>
                            {r.room_name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Floor {r.floor} · {r.room_type}
                        </Text>
                    </div>
                </div>
            )
        },
        {
            title: 'Current Guest',
            width: 160,
            render: r => (
                <div>
                    <Text strong style={{ fontSize: 13 }}>{r.current_guest}</Text>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {r.current_guests_count} guests · {r.payment_mode}
                    </div>
                </div>
            )
        },
        {
            title: 'Status',
            width: 120,
            render: r => <StatusTag status={r.current_status} />
        },
        {
            title: 'Bookings',
            width: 90,
            align: 'center',
            render: r => (
                <div style={{
                    background: '#f0fdf4', borderRadius: 20, padding: '3px 12px',
                    display: 'inline-block', color: '#16a34a', fontWeight: 700, fontSize: 14
                }}>
                    {r.total_bookings}
                </div>
            )
        },
        {
            title: 'Service Requests',
            width: 200,
            render: r => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        background: r.total_sr > 0 ? '#fef3c7' : '#f8fafc',
                        color: r.total_sr > 0 ? '#d97706' : '#94a3b8',
                        borderRadius: 20, padding: '2px 12px',
                        fontWeight: 700, fontSize: 14, minWidth: 36, textAlign: 'center'
                    }}>
                        {r.total_sr}
                    </div>
                    <Progress
                        percent={Math.round((r.total_sr / maxSR) * 100)}
                        showInfo={false}
                        strokeColor={r.total_sr > 2 ? '#f59e0b' : '#6366f1'}
                        trailColor="#e2e8f0"
                        style={{ flex: 1, marginBottom: 0 }}
                        size={['100%', 6]}
                    />
                </div>
            )
        },
        {
            title: 'Total Spend',
            width: 130,
            align: 'center',
            render: r => (
                <div>
                    <Text strong style={{ fontSize: 15, color: r.total_spend > 0 ? '#10b981' : '#94a3b8' }}>
                        ₹{r.total_spend.toLocaleString()}
                    </Text>
                </div>
            )
        },
    ]

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />;
    }

    return (
        <Card

            title={
                <div style={{ margin: 14 }}>
                    <Title level={3} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
                        Service Requests
                    </Title>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                        Room-wise service consumption, guest activity & expense overview
                    </Text>
                </div>}
        >

            {/* ── Header ── */}


            {/* ── Stats Row ── */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={12} md={6}>
                    <MetricCard icon={<HomeOutlined />} label="Total Rooms" value={stats.totalRooms} color="#6366f1" />
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <MetricCard icon={<ShoppingOutlined />} label="Total Requests" value={stats.totalSR} color="#f59e0b"
                        sub={`${stats.pendingSR} pending`} />
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <MetricCard icon={<RiseOutlined />} label="Service Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} color="#10b981" />
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <MetricCard icon={<ThunderboltOutlined />} label="Most Active Room" value={stats.topRoom} color="#ec4899" />
                </Col>
            </Row>

            {/* ── Filters ── */}
            <Card
                style={{ marginBottom: 20, borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                bodyStyle={{ padding: '16px 20px' }}
            >
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={12} md={7}>
                        <Input
                            placeholder="Search room, guest name..."
                            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            allowClear
                            style={{ borderRadius: 10, background: '#f8fafc' }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={7}>
                        <RangePicker
                            // onChange={setDateRange}
                            format="DD-MM-YYYY"
                            onChange={(dates) => {
                                setDateRange(dates); // same dayjs objects as before
                            }}
                            style={{ width: '100%', borderRadius: 10, background: '#f8fafc' }}
                            placeholder={['Check-in From', 'Check-in To']}
                            suffixIcon={<CalendarOutlined style={{ color: '#94a3b8' }} />}
                        />
                    </Col>
                    <Col xs={12} sm={8} md={5}>
                        <Select
                            value={statusFilter}
                            onChange={setStatus}
                            style={{ width: '100%', borderRadius: 10 }}
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="all">All Statuses</Option>
                            <Option value="occupied">Occupied</Option>
                            <Option value="check_out">Checked Out</Option>
                            <Option value="reserved">Reserved</Option>
                            <Option value="cancel">Cancelled</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={8} md={5}>
                        <Select
                            value={sortBy}
                            onChange={setSortBy}
                            style={{ width: '100%', borderRadius: 10 }}
                            suffixIcon={<SortAscendingOutlined />}
                        >
                            <Option value="requests_desc">Most Requests ↓</Option>
                            <Option value="requests_asc">Least Requests ↑</Option>
                            <Option value="spend_desc">Highest Spend ↓</Option>
                            <Option value="spend_asc">Lowest Spend ↑</Option>
                            <Option value="bookings_desc">Most Bookings ↓</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* ── Main Table ── */}
            <Card
                style={{ borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={columns}
                    dataSource={filtered}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} rooms` }}
                    size="middle"
                    rowKey="key"
                    locale={{ emptyText: <Empty description="No rooms found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    expandable={{
                        expandedRowRender: r => renderLogDetail(r.logs),
                        expandedRowKeys: expandedRow ? [expandedRow] : [],
                        onExpand: (expanded, r) => setExpandedRow(expanded ? r.key : null),
                        rowExpandable: r => r.logs?.length > 0,
                    }}
                    rowClassName={(r) => r.total_sr > 0 ? 'sr-highlight-row' : ''}
                    style={{ borderRadius: 14, overflow: 'hidden' }}
                />
            </Card>

            <style>{`
        .ant-table-row.sr-highlight-row > td:first-child {
          border-left: 3px solid #f59e0b !important;
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          color: #64748b !important;
          letter-spacing: 0.04em !important;
          text-transform: uppercase !important;
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .ant-table-tbody > tr:hover > td {
          background: #f0f9ff !important;
        }
        .ant-table-expanded-row > td {
          background: #f8fafc !important;
        }
        .ant-progress-inner {
          border-radius: 4px !important;
        }
      `}</style>
        </Card>
    )
}

export default ServiceReqPage